import type { ExtensionViewSurface } from "@sylviq/ui-react-web/blocks/extension";
import type { CloseReview, CloseReviewCorrectionKind } from "@/lib/api";

export type CultivationFieldSource = "Inherited" | "From evidence" | "Manual" | "Overridden";

export interface CultivationDiffRow {
  key: string;
  label: string;
  saved: string;
  current: string;
  source: CultivationFieldSource;
}

const EVIDENCE_PREFIXES = ["inventory_", "adjustment_", "excise_", "quantity_"];

/** Labels captured from the extension-owned form. The host never authors them. */
export function formLabelsFromSurface(surface: ExtensionViewSurface | null | undefined): Record<string, string> {
  const labels: Record<string, string> = {};
  const walk = (value: unknown): void => {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) {
      for (const item of value) walk(item);
      return;
    }
    const node = value as Record<string, unknown>;
    if (node.type === "form" && Array.isArray(node.fields)) {
      for (const field of node.fields) {
        if (!field || typeof field !== "object" || Array.isArray(field)) continue;
        const item = field as Record<string, unknown>;
        if (typeof item.name === "string" && typeof item.label === "string" && item.label.trim()) {
          labels[item.name] = item.label.trim();
        }
      }
    }
    for (const child of Object.values(node)) walk(child);
  };
  walk(surface?.body);
  return labels;
}

export function humanizeFieldName(name: string): string {
  return name
    .replace(/^allocation_(\d+)_/, "Allocation $1 ")
    .replaceAll("_", " ")
    .replace(/^./, (value) => value.toUpperCase());
}

export function parseSavedAllocations(values: Record<string, string>): Array<{
  pool: string;
  account: string;
  name: string;
  amount: string;
  percent: string;
  labor: string;
}> {
  const rows = new Map<number, { pool: string; account: string; name: string; amount: string; percent: string; labor: string }>();
  for (const [key, value] of Object.entries(values)) {
    const match = /^allocation_(\d+)_(pool|account|name|amount|percent|labor)$/.exec(key);
    if (!match) continue;
    const index = Number(match[1]);
    const field = match[2] as "pool" | "account" | "name" | "amount" | "percent" | "labor";
    const row = rows.get(index) ?? { pool: "", account: "", name: "", amount: "", percent: "", labor: "" };
    row[field] = value;
    rows.set(index, row);
  }
  return [...rows.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, row]) => row);
}

export function sourceForField(
  key: string,
  saved: string | undefined,
  current: string,
  inherited?: Record<string, string> | null,
): CultivationFieldSource {
  if (saved !== undefined && saved !== current) return "Overridden";
  if (inherited && inherited[key] === current) return "Inherited";
  if (EVIDENCE_PREFIXES.some((prefix) => key.startsWith(prefix))) return "From evidence";
  return "Manual";
}

/** Changed values since the saved review. Draft wins; the host only renders the diff. */
export function diffReviewValues(
  saved: Record<string, string> | null | undefined,
  current: Record<string, string>,
  labels: Record<string, string> = {},
  inherited?: Record<string, string> | null,
): CultivationDiffRow[] {
  if (!saved) return [];
  const keys = new Set([...Object.keys(saved), ...Object.keys(current)]);
  const rows: CultivationDiffRow[] = [];
  for (const key of [...keys].sort()) {
    const before = saved[key] ?? "";
    const after = current[key] ?? "";
    if (before === after) continue;
    rows.push({
      key,
      label: labels[key] ?? humanizeFieldName(key),
      saved: before,
      current: after,
      source: sourceForField(key, saved[key], after, inherited),
    });
  }
  return rows;
}

export interface CorrectionMetadata {
  correction_of_review_id?: string;
  correction_kind?: CloseReviewCorrectionKind;
  correction_reason?: string;
  correction_mode: "none" | "true_up" | "reopen";
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function correctionMetadataFromValues(values: Record<string, string>): CorrectionMetadata {
  const raw = values.correction_mode?.trim();
  const correction_mode = raw === "true_up" || raw === "reopen" ? raw : "none";
  if (correction_mode === "none") return { correction_mode };
  const original = values.correction_original_review_id?.trim() ?? "";
  const reason = values.correction_reason?.trim() ?? "";
  return {
    correction_mode,
    ...(UUID.test(original) ? { correction_of_review_id: original } : {}),
    ...(reason ? { correction_reason: reason } : {}),
    correction_kind: correction_mode,
  };
}

export interface CultivationApprovalRequirement {
  kind: string;
  ref?: string;
  reason?: string;
  missing?: boolean;
}

export interface CultivationApprovalSummary {
  approval_required: boolean;
  reasons: string[];
}

export function approvalSummaryFromResult(result: Record<string, unknown> | null | undefined): CultivationApprovalSummary {
  if (!result) return { approval_required: false, reasons: [] };
  const raw = Array.isArray(result.approval_requirements)
    ? result.approval_requirements
    : Array.isArray((result.requirements as Record<string, unknown> | undefined)?.reasons)
      ? ((result.requirements as Record<string, unknown>).reasons as unknown[])
      : [];
  const reasons: string[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      if (item.trim()) reasons.push(item.trim());
      continue;
    }
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const requirement = item as Record<string, unknown>;
    const kind = typeof requirement.kind === "string" ? requirement.kind : "approval";
    const reason = typeof requirement.reason === "string" ? requirement.reason : "";
    const ref = typeof requirement.ref === "string" ? requirement.ref : "";
    reasons.push(`${kind}: ${reason || ref || "approval reference is required"}`);
  }
  const requirements = result.requirements as Record<string, unknown> | undefined;
  if (requirements?.approval_required === true && reasons.length === 0) {
    reasons.push("Approval is required before posting this review.");
  }
  return { approval_required: reasons.length > 0 || requirements?.approval_required === true, reasons };
}

export function reviewIsReadOnly(review: CloseReview | null | undefined): boolean {
  return review?.state === "posted" || review?.state === "locked" || review?.state === "posting";
}

export function reviewNeedsCorrection(review: CloseReview | null | undefined): boolean {
  return review?.state === "correction_required";
}

export function previewFingerprint(input: {
  values: Record<string, string>;
  evidenceKey: string;
  periodStart: string;
  periodEnd: string;
}): string {
  const values = Object.keys(input.values)
    .sort()
    .map((key) => [key, input.values[key]]);
  return JSON.stringify([values, input.evidenceKey, input.periodStart, input.periodEnd]);
}

export interface CultivationPreviewSection {
  id: string;
  title: string;
  status: string;
  columns: string[];
  rows: string[][];
  warnings: string[];
}

export interface CultivationPreviewJournal {
  family: string;
  name?: string;
  line_count?: number;
  debit_total_cents: number;
  credit_total_cents: number;
  residual_cents?: number;
  balanced: boolean;
  error?: string;
}

export interface CultivationPreview {
  status: "blocked" | "needs_review" | "reviewable" | string;
  input_identity: string;
  blockers: string[];
  warnings: string[];
  journals: CultivationPreviewJournal[];
  sections: CultivationPreviewSection[];
  requirements: { approval_required: boolean; reasons: string[] };
}

export function parseCultivationPreview(result: Record<string, unknown>): CultivationPreview {
  const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  const journals = Array.isArray(result.journals)
    ? result.journals.flatMap((item): CultivationPreviewJournal[] => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return [];
      const journal = item as Record<string, unknown>;
      if (typeof journal.family !== "string") return [];
      return [{
        family: journal.family,
        ...(typeof journal.name === "string" ? { name: journal.name } : {}),
        ...(typeof journal.line_count === "number" ? { line_count: journal.line_count } : {}),
        debit_total_cents: typeof journal.debit_total_cents === "number" ? journal.debit_total_cents : 0,
        credit_total_cents: typeof journal.credit_total_cents === "number" ? journal.credit_total_cents : 0,
        ...(typeof journal.residual_cents === "number" ? { residual_cents: journal.residual_cents } : {}),
        balanced: journal.balanced === true,
        ...(typeof journal.error === "string" ? { error: journal.error } : {}),
      }];
    })
    : [];
  const sections = Array.isArray(result.sections)
    ? result.sections.flatMap((item): CultivationPreviewSection[] => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return [];
      const section = item as Record<string, unknown>;
      if (typeof section.id !== "string" || typeof section.title !== "string") return [];
      const columns = Array.isArray(section.columns) ? section.columns.filter((value): value is string => typeof value === "string") : [];
      const rows = Array.isArray(section.rows)
        ? section.rows.flatMap((row): string[][] => Array.isArray(row)
          ? [row.map((value) => typeof value === "string" ? value : String(value ?? ""))]
          : [])
        : [];
      const sectionWarnings = Array.isArray(section.warnings) ? section.warnings.filter((value): value is string => typeof value === "string") : [];
      return [{
        id: section.id,
        title: section.title,
        status: typeof section.status === "string" ? section.status : "needs_review",
        columns,
        rows,
        warnings: sectionWarnings,
      }];
    })
    : [];
  const requirements = result.requirements as Record<string, unknown> | undefined;
  return {
    status: typeof result.status === "string" ? result.status : "blocked",
    input_identity: typeof result.input_identity === "string" ? result.input_identity : "",
    blockers: strings(result.blockers),
    warnings: strings(result.warnings),
    journals,
    sections,
    requirements: {
      approval_required: requirements?.approval_required === true,
      reasons: strings(requirements?.reasons),
    },
  };
}

export function formatCents(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const absolute = Math.abs(Math.trunc(value));
  return `${sign}$${Math.floor(absolute / 100).toLocaleString("en-US")}.${String(absolute % 100).padStart(2, "0")}`;
}
